import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useLocation, useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { initialBancoPagamentoFiltersValues } from "../hooks/banco_pagamento_lista/filters";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useSaldoBancarioCadastroFilters } from "../hooks/saldo_bancario_cadastro/filters";
import { useSaldoBancarioCadastroPagination } from "../hooks/saldo_bancario_cadastro/pagination";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { SaldoBancarioCadastroLista } from "../../domain/models/saldo_bancario_cadastro_lista";
import { SaldoBancarioCadastroForm, SaldoBancarioCadastroSchema } from "../schemas/SaldoBancario_cadastro/SaldoBancario_cadastro";
import { BancoPagamento } from "../../domain/models/banco_pagamento";

type LoadSaldoBancarioCadastroState = {
    idsCentroCusto?: number[];
    idsFornecedor?: number[];
    idsFuncionario?: number[];
    idsSubconta?: number[];
    titulo?: string;
    nrConta?: number;
    dataDe?: Date;
    dataAte?: Date;
    tipo?: number;
    valor?: number;

    page?: number;
    rowsPerPage?: number;

}

export const useSaldoBancarioCadastroViewModel = () => {

    const {
        financeiro: {
            createSaldoBancarioUseCase,
            getAllSaldoBancarioUseCase,
            getBancoPagamentoByIdUseCase,

        },
        commons: {
            getCentroCustoDropDownUseCase,
            getSubcontasDropDownUseCase,
            getFornecedorDropDownUseCase,
            getFuncionariosDropDownUseCase,
            getBancosPagamentoDropDownUseCase,
        },

    } = useDependencyInjection();

    const hookForm = useForm<SaldoBancarioCadastroForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        resolver: zodResolver(SaldoBancarioCadastroSchema),

    });

    const location = useLocation();
    const navigate = useNavigate();
    const { pushErrorNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const debouncedRef = useCreateDebounceHandler(1500);

    const [selectedIdBancoPagamento, setSelectedIdBancoPagamento] = useState<number>();
    const [openSaldoBancarioCadastroModal, setOpenSaldoBancarioCadastroCadastroModal] = useState(false);
    const [openSaldoBancarioTransferenciaModal, setOpenSaldoBancarioTransferenciaModal] = useState(false);
    const [saldoBancarioLista, setSaldoBancarioLista] = useState<SaldoBancarioCadastroLista[]>([]);
    const [bancoPagamento, setBancoPagamento] = useState<BancoPagamento>();


    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();


    const filters = useSaldoBancarioCadastroFilters();
    const pagination = useSaldoBancarioCadastroPagination();

    //DROPDOWN ESTADOS

    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
    const [ddlSubconta, setDdlSubconta] = useState<DropDown[]>();
    const [ddlFornecedor, setDdlFornecedor] = useState<DropDown[]>();
    const [ddlFuncionario, setDdlFuncionario] = useState<DropDown[]>();
    const [ddlBancosPagamento, setDdlBancosPagamento] = useState<DropDown[]>();


    //#region CARREGAMENTO.

    const loadBancoPagamentoInfos = useCallback(async (idBancoPagamento: number) => {
        try {

            const selectedBanco = await getBancoPagamentoByIdUseCase.getBancoPagamentoById(idBancoPagamento);
            if (selectedBanco)
                setBancoPagamento(selectedBanco);

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar informações do banco!'));
        }

    }, [
        getBancoPagamentoByIdUseCase,
        pushErrorNotification,
    ]);

    const loadSaldoBancarioLista = useCallback(async (funcState?: LoadSaldoBancarioCadastroState, idBancoPagamentoParam?: number) => {

        try {
            const idBancoPagamento = selectedIdBancoPagamento ?? idBancoPagamentoParam

            const filteredIdBancoPagamento = idBancoPagamento ?? filters.idBancoPagamento;
            const filteredTipo = evaluateParamValue(funcState?.tipo, filters.tipo);
            const filteredTitulo = evaluateParamValue(funcState?.titulo, filters.titulo);
            const filteredValor = evaluateParamValue(funcState?.valor, filters.valor);
            const filteredNrConta = evaluateParamValue(funcState?.nrConta, filters.nrConta);

            const filteredDataDe = evaluateParamValue(funcState?.dataDe, filters.dataDe);
            const filteredDataAte = evaluateParamValue(funcState?.dataAte, filters.dataAte);

            const filteredIdsCentroCusto = funcState?.idsCentroCusto ?? filters.idsCentroCusto;
            const filteredIdsSubconta = funcState?.idsSubconta ?? filters.idsSubconta;
            const filteredIdsFornecedor = funcState?.idsFornecedor ?? filters.idsFornecedor;
            const filteredIdsFuncionario = funcState?.idsFuncionario ?? filters.idsFuncionario;

            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllSaldoBancarioUseCase.getAllSaldoBancarioLista(
                {
                    idBancoPagamento: filteredIdBancoPagamento,
                    titulo: filteredTitulo ?? "",
                    valor: filteredValor ?? undefined,
                    nrConta: filteredNrConta ?? undefined,
                    tipo: filteredTipo ?? undefined,
                    dataDe: filteredDataDe ?? undefined,
                    dataAte: filteredDataAte ?? undefined,

                    idsCentroCusto: filteredIdsCentroCusto,
                    idsSubconta: filteredIdsSubconta,
                    idsFornecedor: filteredIdsFornecedor,
                    idsFuncionario: filteredIdsFuncionario,


                },
                filteredPage,
                filteredRowsPerPage
            );

            setSaldoBancarioLista(getAllSaldoBancarioUseCase.items);

            pagination.setRowsPerPage(getAllSaldoBancarioUseCase.pageSize);
            pagination.setTotalItems(getAllSaldoBancarioUseCase.totalItems);
        } catch (e: unknown) {
            setSaldoBancarioLista([]);
            pushErrorNotification('Error', processError(e, 'Falha ao buscar saldo bancarios!'));
        }
    }, [
        filters,
        pagination,
        getAllSaldoBancarioUseCase,
        selectedIdBancoPagamento,
        pushErrorNotification,
    ]);


    const loadCentroCustoDropDown = useCallback(async () => {

        try {
            setPageLoading(true);
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centro de custo!'));
        } finally {
            setPageLoading(false);
        }

    }, [getCentroCustoDropDownUseCase, pushErrorNotification, setPageLoading]);

    const loadSubcontaDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getSubcontasDropDownUseCase.getSubcontasDropDown();
            setDdlSubconta(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar subcontas!'));
        } finally {
            setPageLoading(false);
        }
    }, [getSubcontasDropDownUseCase, pushErrorNotification, setPageLoading]);


    const loadFornecedorDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getFornecedorDropDownUseCase.getFornecedorDropDown();
            setDdlFornecedor(
                result.map((x) => ({
                    id: x.idFornecedor,
                    title: x.nomeFantasia
                }))
            );

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar fornecedores!'));
        } finally {
            setPageLoading(false);
        }
    }, [getFornecedorDropDownUseCase, pushErrorNotification, setPageLoading]);


    const loadFuncionarioDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getFuncionariosDropDownUseCase.getFuncionariosDropDown();
            setDdlFuncionario(result.map((x) => ({
                id: x.idFuncionario,
                title: x.nome
            })));

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar funcionários!'));
        } finally {
            setPageLoading(false);
        }
    }, [getFuncionariosDropDownUseCase, pushErrorNotification, setPageLoading]);

    const loadBancosPagamentoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getBancosPagamentoDropDownUseCase.getBancosPagamentoDropDown();
            setDdlBancosPagamento(result);

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar bancos de pagamento!'));
        } finally {
            setPageLoading(false);
        }
    }, [getBancosPagamentoDropDownUseCase, pushErrorNotification, setPageLoading]);

    //#endregion



    usePageStartBootstrap(async () => {
        try {

            await loadCentroCustoDropDown();
            await loadSubcontaDropDown();
            await loadFornecedorDropDown();
            await loadFuncionarioDropDown();
            await loadBancosPagamentoDropDown();

            const idBancoPagamentoParam = selectedIdBancoPagamento ?? location.state?.idBancoPagamento as number

            if (idBancoPagamentoParam !== undefined) {

                await loadSaldoBancarioLista(undefined, idBancoPagamentoParam);
                await loadBancoPagamentoInfos(idBancoPagamentoParam);
                setSelectedIdBancoPagamento(idBancoPagamentoParam);
                filters.setIdBancoPagamento(idBancoPagamentoParam);
            }

        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar saldo bancários!")[0]);
        }
    })





    //#region CADASTRO 

    const handleBancoPagamentoEditOpenState = useCallback(async (openState: boolean) => {
        setOpenSaldoBancarioCadastroCadastroModal(openState);
        if (openState && selectedIdBancoPagamento) {
            hookForm.reset()
            hookForm.setValue('idBancoPagamento', selectedIdBancoPagamento)
        } else {
            hookForm.reset()
        }

    }, [hookForm, selectedIdBancoPagamento]);


    const handleBancoTransferenciaOpenState = useCallback(async (openState: boolean) => {
        setOpenSaldoBancarioTransferenciaModal(openState);
        if (openState && selectedIdBancoPagamento) {
            hookForm.reset()
            hookForm.setValue('idBancoPagamento', selectedIdBancoPagamento)
        } else {
            hookForm.reset()
        }
    }, [hookForm, selectedIdBancoPagamento]);


    const handleSaldoBancarioCadastroSubmit = useCallback(async (form: SaldoBancarioCadastroForm) => {
        try {

            setSaving(true);
            if (selectedIdBancoPagamento) {
                await createSaldoBancarioUseCase.createSaldoBancario({
                    idBancoPagamento: selectedIdBancoPagamento,
                    idBancoTransferencia: form.idBancoTransferencia,
                    tipo: form.tipo,
                    valor: form.valor,
                    dataTransferencia: form.dataTransferencia,
                    cadastroMovimento: form.cadastroMovimento,
                })
                await loadSaldoBancarioLista();
                setOpenSaldoBancarioCadastroCadastroModal(false);
                setOpenSaldoBancarioTransferenciaModal(false);
                hookForm.reset();
                pushInfoNotification("Sucesso", "saldo salvo com sucesso!", { isAutoFinish: true });

            } else {
                pushErrorNotification("Erro", processError('Erro', "Falha ao salvar saldo bancário!"));
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar saldo bancário!"));
        } finally {
            setSaving(false);
        }
    }, [
        createSaldoBancarioUseCase,
        selectedIdBancoPagamento,
        hookForm,
        loadSaldoBancarioLista,
        pushErrorNotification,
        pushInfoNotification
    ]);


    //#endregion





    //#region PAGINAÇÃO.
    const handlePageBack = useCallback(() => {
        navigate('/SaldoBancario_lista')
    }, [navigate]);

    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadSaldoBancarioLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de saldo bancario!'));
        } finally {
            setLoading(false);
        }
    }, [
        loadSaldoBancarioLista,
        pushErrorNotification,
    ]);


    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadSaldoBancarioLista({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadSaldoBancarioLista, pushErrorNotification]);


    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadSaldoBancarioLista({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadSaldoBancarioLista, pushErrorNotification]);

    //#endregion






    //#region PESQUISA.


    const handleIdCentroCustoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setIdsCentroCusto(newValues)
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadSaldoBancarioLista({ idsCentroCusto: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar saldos!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadSaldoBancarioLista,]
    );

    const handleTipoChange = useDebouncedCallback(debouncedRef, (tipo: number) => {
        filters.setTipo(tipo)
        return async () => {
            try {
                setLoading(true);
                pagination.setPage(1);
                await loadSaldoBancarioLista({ tipo, page: 1 });
            } finally {
                setLoading(false);
            }
        };
    }, [loadSaldoBancarioLista]);


    const handleIdSubcontaChange = useDebouncedCallback(

        debouncedRef,
        (newValues: number[]) => {
            filters.setIdsSubconta(newValues)
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadSaldoBancarioLista({ idsSubconta: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar saldos!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadSaldoBancarioLista,]
    );

    const handleTituloChange = useDebouncedCallback(
        debouncedRef,
        (item: string | null) => {
            const titulo = item ?? undefined
            filters.setTitulo(titulo);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadSaldoBancarioLista({ titulo, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar saldos!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadSaldoBancarioLista, filters]
    );


    const handleDataRangeChange = useDebouncedCallback(
        debouncedRef,
        (de: Date | null, ate: Date | null) => {
            const dataDe = de ?? undefined;
            const dataAte = ate ?? undefined;

            filters.setDataDe(dataDe);
            filters.setDataAte(dataAte);

            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadSaldoBancarioLista({ dataDe, dataAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar Saldos!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadSaldoBancarioLista, filters, pagination]
    );


    const handleIdFornecedorChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setIdsFornecedor(newValues);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadSaldoBancarioLista({ idsFornecedor: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar Saldos!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadSaldoBancarioLista]
    );



    const handleIdFuncionarioChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setIdsFuncionario(newValues);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadSaldoBancarioLista({ idsFuncionario: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar saldos!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadSaldoBancarioLista]
    );


    const handleValorChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            const valorNormalizado =
                newValue && !isNaN(Number(newValue)) && newValue.trim() !== ""
                    ? Number(newValue)
                    : undefined;

            filters.setValor(valorNormalizado);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadSaldoBancarioLista({ valor: valorNormalizado, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar saldo!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadSaldoBancarioLista]
    );

    const handleNrContaChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            const valorNormalizado =
                newValue && !isNaN(Number(newValue)) && newValue.trim() !== ""
                    ? Number(newValue)
                    : undefined;

            filters.setNrConta(valorNormalizado);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadSaldoBancarioLista({ nrConta: valorNormalizado, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar saldo!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadSaldoBancarioLista]
    );

    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            filters.setValor(undefined);
            pagination.setPage(1);
            await loadSaldoBancarioLista({
                ...initialBancoPagamentoFiltersValues,
                page: 1
            }, selectedIdBancoPagamento);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar bancos!'));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        pagination,
        selectedIdBancoPagamento,
        loadSaldoBancarioLista,
        pushErrorNotification
    ]);


    //#endregion 


    return {
        loading,
        saving,
        errorMessage,
        saldoBancarioLista,
        hookForm,
        filters,
        pagination,
        ddlCentroCusto,
        ddlSubconta,
        ddlFornecedor,
        ddlFuncionario,
        openSaldoBancarioCadastroModal,
        openSaldoBancarioTransferenciaModal,
        bancoPagamento,
        ddlBancosPagamento: ddlBancosPagamento ?? [],

        handleBancoTransferenciaOpenState,
        handlePageBack,
        handleNrContaChange,
        handleValorChange,
        handleIdCentroCustoChange,
        handleTituloChange,
        handleDataRangeChange,
        handleIdFornecedorChange,
        handleIdFuncionarioChange,
        handleIdSubcontaChange,
        handleBancoPagamentoEditOpenState,
        handleSaldoBancarioCadastroSubmit,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleClearFilters,
        handleTipoChange,
    };
}
